import { useState } from "react";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { usePayPeriodNoteAction, usePayPeriodNotes } from "./query";

const EMPTY_DRAFT = {
  body: "",
  gridText: null as string | null,
  error: null as string | null,
  isOpen: false,
};

type NoteDraft = typeof EMPTY_DRAFT;

export const usePayrollNoteComposer = ({
  businessId,
  periodStart,
  userId,
  enabled,
}: {
  businessId: string | null;
  periodStart: string | undefined;
  userId: string | undefined;
  enabled: boolean;
}) => {
  const scope = JSON.stringify([businessId, periodStart, userId]);
  const [drafts, setDrafts] = useState<Record<string, NoteDraft>>({});
  const draft = drafts[scope] ?? EMPTY_DRAFT;
  const query = usePayPeriodNotes(businessId, periodStart ?? null, {
    enabled,
    poll: enabled && draft.isOpen,
  });
  const action = usePayPeriodNoteAction(businessId, periodStart ?? "");
  const nextPeriod = query.data?.nextPeriod ?? null;
  const isAvailable = enabled && !!nextPeriod && !query.isError;
  const periodError = query.isError
    ? "The next open pay period could not be checked."
    : query.isSuccess && !nextPeriod
      ? "The next open period does not match the payroll schedule. Review the payroll schedule before saving this note."
      : null;

  const update = (changes: Partial<NoteDraft>) =>
    setDrafts((current) => ({
      ...current,
      [scope]: { ...(current[scope] ?? EMPTY_DRAFT), ...changes },
    }));

  const setGridText = (text: string | null) => {
    if (text && text.length > 200_000) {
      update({
        error:
          "Copy fewer cells. Grid content must be 200,000 characters or less.",
      });
      return;
    }
    update({ gridText: text, error: null });
  };

  const open = (gridText?: string) => {
    if (!enabled) return;
    if (gridText && gridText.length > 200_000) {
      toast.error(
        "Copy fewer cells. Grid content must be 200,000 characters or less.",
      );
      return;
    }
    update({
      isOpen: true,
      error: null,
      ...(gridText === undefined ? {} : { gridText }),
    });
  };

  const setOpen = (isOpen: boolean) => {
    if (!action.isPending) update({ isOpen });
  };

  const save = () => {
    if (
      !isAvailable ||
      action.isPending ||
      (!draft.body.trim() && !draft.gridText?.trim())
    )
      return;
    update({ error: null });
    action.mutate(
      { action: "create", body: draft.body, grid_text: draft.gridText },
      {
        onSuccess: () => {
          setDrafts((current) => {
            const remaining = { ...current };
            delete remaining[scope];
            return remaining;
          });
          toast.success("Note saved for the next open period");
        },
        onError: (cause) => {
          setDrafts((current) =>
            current[scope]
              ? {
                  ...current,
                  [scope]: { ...current[scope], error: getErrorMessage(cause) },
                }
              : current,
          );
        },
      },
    );
  };

  return {
    ...draft,
    isOpen: enabled && draft.isOpen,
    nextPeriod,
    canOpen: enabled,
    isAvailable,
    periodError,
    isCheckingPeriod: query.isFetching,
    retryPeriod: () => {
      void query.refetch();
    },
    isPending: action.isPending,
    open,
    setOpen,
    setBody: (body: string) => update({ body, error: null }),
    setGridText,
    save,
  };
};

export type PayrollNoteComposerState = ReturnType<
  typeof usePayrollNoteComposer
>;
